import React, { SyntheticEvent } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { useHistory } from 'react-router-dom';
import { BSModal } from '~/components/Bootstrap';
import { NavTabs } from '~/components/Patient';
import { Search, formValsReducer } from '~/components/Search';
import { SearchColumns } from '~/components/SearchColumns';
import {
    SearchColumn,
    SearchContext,
    SearchContextType,
    ServicesContext,
    useSearchClinic,
    useSearchPCP,
    useSearchSort,
} from '~/services';
import { mapPCPs, prepareSearchQueryString } from '~/util';

function SearchFiltersModal() {
    const context = React.useContext(ServicesContext);
    const {
        clinicList,
        pcpList,
        searchValues,
        setSearchValues,
        resetPagination,
        selectedPreset,
        setCustomColumns,
        setSelectedPreset,
        columnPresets,
    } = React.useContext(SearchContext) as SearchContextType;
    const getPresetColumns = React.useCallback(
        (preset: string) => columnPresets.find((x) => x.label === preset)?.columns || [],
        [columnPresets]
    );
    const history = useHistory();
    const { resetSort } = useSearchSort();

    // filter state
    const { clinic, setClinic } = useSearchClinic();
    const { PCP, setPCP } = useSearchPCP();
    const [_clinic, _setClinic] = React.useState(clinic);
    const [_PCP, _setPCP] = React.useState(PCP);
    const [_PCPList, _setPCPList] = React.useState(pcpList);
    const [formVals, setFormVals] = React.useReducer(
        formValsReducer,
        JSON.parse(JSON.stringify(searchValues))
    );

    // column preset state
    const [_selectedPreset, _setSelectedPreset] = React.useState(selectedPreset);
    const [orderedColumns, setOrderedColumns] = React.useState(getPresetColumns(selectedPreset));

    // change the selected preset to Custom whenever the orderedColumns change
    const updateOrderedColumns = React.useCallback(
        (updatedColumns: SearchColumn[]) => {
            setOrderedColumns(updatedColumns);
            _setSelectedPreset('Custom');
        },
        [setOrderedColumns, _setSelectedPreset]
    );

    // if a preset other than Custom is selected, update orderedColumns
    React.useEffect(() => {
        if (_selectedPreset !== 'Custom') {
            setOrderedColumns(getPresetColumns(_selectedPreset));
        }
    }, [_selectedPreset, getPresetColumns]);

    const [show, setShow] = React.useState(false);
    const [page, setPage] = React.useState('filters');

    React.useEffect(() => {
        // Update the PCP list any time the clinic changes
        _setPCPList(mapPCPs(_clinic !== null && _clinic.length > 0 ? _clinic : clinicList));
    }, [_clinic, clinicList]);

    function resetFormValsToSearchVals() {
        setFormVals({
            target: {
                id: 'formvals',
                value: null,
                formVals: JSON.parse(JSON.stringify(searchValues)),
            },
        });
    }

    function onHide() {
        // Cancel action, so revert the form
        resetFormValsToSearchVals();
        setShow(false);
    }

    function onShow() {
        _setClinic(clinic);
        _setPCP(PCP);
        _setPCPList(pcpList);
        _setSelectedPreset(selectedPreset);
        setOrderedColumns(getPresetColumns(selectedPreset));
        // load the latest search vals, in case we had
        // an external update to the search from MIC
        resetFormValsToSearchVals();
        setPage('filters');
        setShow(true);
    }

    const clearFilters = React.useCallback(() => {
        _setClinic(null);
        _setPCP(null);
        setFormVals('clear');
    }, [_setClinic, _setPCP, setFormVals]);

    function clearColumns() {
        updateOrderedColumns([]);
    }

    function handleClear() {
        if (page === 'filters') clearFilters();
        if (page === 'columns') clearColumns();
    }

    function submit(e: SyntheticEvent) {
        e.preventDefault();
        resetPagination();
        resetSort();
        setSearchValues({ ...formVals });
        setClinic(_clinic);
        setPCP(_PCP);
        setSelectedPreset(_selectedPreset);
        if (_selectedPreset === 'Custom') {
            setCustomColumns(orderedColumns);
        }
        history.push(
            `/patients?${prepareSearchQueryString({
                ...formVals,
                clinic,
                PCP,
                redirect: false,
            })}`
        );
        setShow(false);
    }

    return (
        <>
            <Button variant="primary" onClick={onShow}>
                <i className="fa-solid fa-filter" /> Filters
            </Button>
            <BSModal
                className="search-filters-modal"
                show={show}
                onHide={onHide}
                aria-labelledby="search-filters-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="search-filters-title" role="heading" aria-level={1}>
                        Filters
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <NavTabs
                        tabs={[
                            { label: 'Filters', val: 'filters' },
                            { label: 'Columns', val: 'columns' },
                        ]}
                        page={page}
                        setPage={setPage}
                    />
                    <div className="py-3">
                        {page === 'filters' && (
                            <Search
                                formVals={formVals}
                                setFormVals={setFormVals}
                                clinic={_clinic}
                                setClinic={_setClinic}
                                PCP={_PCP}
                                setPCP={_setPCP}
                                pcpList={_PCPList}
                                clearFilter={clearFilters}
                            />
                        )}
                        {page === 'columns' && (
                            <DndProvider
                                backend={'ontouchstart' in window ? TouchBackend : HTML5Backend}
                            >
                                <SearchColumns
                                    selectedPreset={_selectedPreset}
                                    setSelectedPreset={_setSelectedPreset}
                                    orderedColumns={orderedColumns.filter(
                                        (col) =>
                                            !(
                                                context.user.hasFeatureToggle('hidden_foc') &&
                                                col.substring(0, 3) === 'AW'
                                            )
                                    )}
                                    updateOrderedColumns={updateOrderedColumns}
                                />
                            </DndProvider>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="text-end">
                    <button type="button" className="btn btn-secondary me-2" onClick={handleClear}>
                        {page === 'filters' && 'Clear'}
                        {page === 'columns' && 'Remove Columns'}
                    </button>
                    <button
                        type="submit"
                        form="patient-search"
                        className="btn btn-primary"
                        onClick={submit}
                    >
                        {page === 'filters' && 'Search'}
                        {page === 'columns' && 'Apply'}
                    </button>
                </Modal.Footer>
            </BSModal>
        </>
    );
}

export { SearchFiltersModal };
